import React from 'react';
import { Badge, Button, Drawer, Input, Space, Tooltip, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  DatabaseOutlined,
  FolderOpenOutlined,
  FormOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Moon, PanelRight, Sun } from 'lucide-react';
import { useChatTheme } from '../../theme/provider.js';
import { buildChatThemeCssVars } from '../../theme/themeCssVars.js';

const { Text, Title } = Typography;

const APPEARANCE_ACTIONS = {
  light: { icon: Moon, label: '切换为深色主题' },
  dark: { icon: Sun, label: '切换为浅色主题' },
};
function WorkspaceThemeToggle() {
  const theme = useChatTheme();
  if (!theme?.toggleAppearance) return null;
  const { icon: Icon, label } = APPEARANCE_ACTIONS[theme.appearance];
  return <Button aria-label={label} title={label} className="flare-workspace-icon-action"
    icon={<Icon size={16} strokeWidth={1.8} />} onClick={theme.toggleAppearance} size="small" type="text" />;
}

function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

function firstTruthy(...values) {
  return values.find(Boolean);
}

/**
 * Pure presentation top bar for project header, workspace tabs, and quick actions.
 *
 * @param {object} props - Top bar render props.
 * @returns {JSX.Element} Top bar UI.
 */
export default function ChatWorkspaceTopBar(props) {
  const {
    hasConversationStarted,
    projectSlot,
    projectNameEditing,
    projectNameDraft,
    setProjectNameDraft,
    handleProjectNameSave,
    handleProjectNameCancel,
    resolvedProjectDisplayName,
    handleProjectNameStartEdit,
    isCompactLayout,
    activeWorkspaceTab,
    setActiveWorkspaceTab,
    segmentedThemeStyle,
    themeTokens,
    resolvedUILabels,
    resolvedProductName,
    customerRoleFallback,
    knowledgeHubPopoverContent,
    knowledgeHubPopoverOpen,
    handleKnowledgeHubOpenChange,
    handleOpenKnowledgeHub,
    workspaceOpenButtonVisible,
    hasUnseenWorkspaceUpdate,
    showCanvasPanel,
    handleToggleWorkspacePanel,
    showSidebarMenuButton,
    handleOpenSidebarMenu,
    showWorkspaceTabSwitch,
    onBackToProject,
  } = { showWorkspaceTabSwitch: true, ...props };
  const labels = Object.assign({}, resolvedUILabels);
  const project = Object.assign({}, projectSlot);
  const tabItems = [
    { label: firstTruthy(labels.tab_chats, '会话'), value: 'chats' },
    { label: firstTruthy(labels.tab_sources, '资料'), value: 'sources' },
  ];
  const activeTabIndex = Math.max(
    0,
    tabItems.findIndex((item) => item.value === activeWorkspaceTab),
  );
  const shouldShowCompactSessionTitle = [isCompactLayout, resolvedProjectDisplayName].every(Boolean);

  return (
    <div
      className={[
        'flare-workspace-topbar',
        conditionalValue(isCompactLayout, 'is-compact', ''),
      ].filter(Boolean).join(' ')}
      style={{ padding: conditionalValue(isCompactLayout, '10px 12px', '14px 24px'),
        background: themeTokens.surfaceBg, borderBottom: `1px solid ${themeTokens.divider}`,
        boxSizing: 'border-box', flexShrink: 0, minHeight: 60, minWidth: 0 }}
    >
      <div className={['flare-workspace-header-switch', conditionalValue(hasConversationStarted, 'is-working', 'is-empty')].join(' ')}>
        <div className="flare-workspace-header-view flare-workspace-header-empty-view">
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              width: '100%',
            }}
          >
            <div className="flare-workspace-empty-project-row" style={{ position: 'relative', width: '100%' }}>
              {conditionalValue(showSidebarMenuButton && !hasConversationStarted, (
                <Button
                  aria-label="打开会话侧栏"
                  className="flare-workspace-icon-action"
                  icon={<MenuOutlined />}
                  onClick={handleOpenSidebarMenu}
                  size="small"
                  style={{ left: 0, position: 'absolute', top: '50%', transform: 'translateY(-50%)', minHeight: 44, minWidth: 44 }}
                  type="text"
                />
              ))}
              <Space
                align="center"
                className="flare-workspace-empty-project-main"
                size={10}
                style={{
                  justifyContent: 'flex-start',
                  paddingLeft: conditionalValue(showSidebarMenuButton, 44, 0),
                  paddingRight: 72,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <span className="flare-workspace-project-icon">
                  {firstTruthy(project.icon, <FolderOpenOutlined style={{ color: themeTokens.iconNeutral }} />)}
                </span>
                {conditionalValue(projectNameEditing, (
                  <Space align="center" size={6}>
                    <Input
                      autoFocus
                      onChange={(event) => setProjectNameDraft(event.target.value)}
                      onPressEnter={handleProjectNameSave}
                      size="small"
                      style={{ width: conditionalValue(isCompactLayout, 160, 220) }}
                      value={projectNameDraft}
                    />
                    <Button onClick={handleProjectNameSave} size="small" type="text">
                      保存
                    </Button>
                    <Button onClick={handleProjectNameCancel} size="small" type="text">
                      取消
                    </Button>
                  </Space>
                ), (
                  <Space align="center" className="flare-workspace-project-title-group" size={8}>
                    <Title
                      className="flare-workspace-project-title"
                      level={conditionalValue(isCompactLayout, 5, 4)}
                      style={{
                        color: themeTokens.textPrimary,
                        fontFamily: themeTokens.fontFamilyHeading,
                        fontWeight: 500,
                        fontSize: 14,
                        letterSpacing: '0.01em',
                        margin: 0,
                      }}
                    >
                      {resolvedProjectDisplayName}
                    </Title>
                    <Button
                      aria-label={resolvedUILabels.edit_project_button || '编辑'}
                      className="flare-workspace-project-edit"
                      icon={<FormOutlined />}
                      onClick={handleProjectNameStartEdit}
                      size="small"
                      title={resolvedUILabels.edit_project_button || '编辑'}
                      type="text"
                    />
                  </Space>
                ))}
              </Space>
              <div className="flare-workspace-status-actions" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
              <WorkspaceThemeToggle />
              <Button
                aria-label="打开资料库"
                className="flare-workspace-icon-action"
                icon={<DatabaseOutlined />}
                onClick={handleOpenKnowledgeHub}
                size="small"
                type="text"
              />
              </div>
            </div>

            {conditionalValue(showWorkspaceTabSwitch, (
              <div
                className="flare-workspace-tab-switch"
                style={segmentedThemeStyle}
                role="tablist"
                aria-label="workspace tabs"
              >
                <span
                  className="flare-workspace-tab-switch-thumb"
                  style={{ transform: `translateX(${activeTabIndex * 100}%)` }}
                />
                {tabItems.map((item) => {
                  const active = item.value === activeWorkspaceTab;
                  return (
                    <button
                      key={item.value}
                      aria-selected={active}
                      className={['flare-workspace-tab-switch-item', conditionalValue(active, 'is-active', '')].filter(Boolean).join(' ')}
                      onClick={() => setActiveWorkspaceTab(item.value)}
                      role="tab"
                      type="button"
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="flare-workspace-header-view flare-workspace-header-working-view">
          <div className="flare-workspace-status-bar">
            <div className="flare-workspace-status-main">
              <Space align="center" size={6}>
                {conditionalValue(showSidebarMenuButton && hasConversationStarted, (
                  <Button
                    aria-label="打开会话侧栏"
                    className="flare-workspace-icon-action"
                    icon={<MenuOutlined />}
                    onClick={handleOpenSidebarMenu}
                    size="small"
                    style={{ minHeight: 44, minWidth: 44 }}
                    type="text"
                  />
                ))}
                {shouldShowCompactSessionTitle ? null : (
                  <Button
                    aria-label="返回项目"
                    className="flare-workspace-icon-action"
                    icon={<ArrowLeftOutlined />}
                    onClick={onBackToProject}
                    size="small"
                    title="返回项目"
                    type="text"
                  />
                )}
                {conditionalValue(shouldShowCompactSessionTitle, (
                  <Text
                    className="flare-workspace-status-compact-title"
                    strong
                    style={{ color: themeTokens.textPrimary, fontSize: 14, fontWeight: 500 }}
                    title={resolvedProjectDisplayName}
                  >
                    {resolvedProjectDisplayName}
                  </Text>
                ), (
                  <Text className="flare-workspace-status-title" strong style={{ color: themeTokens.textPrimary, fontSize: 14, fontWeight: 500 }}>
                    {firstTruthy(resolvedProductName, customerRoleFallback)}
                  </Text>
                ))}
              </Space>
            </div>
            <div className="flare-workspace-status-actions">
              <WorkspaceThemeToggle />
              <Button
                aria-label="打开资料库"
                className="flare-workspace-icon-action"
                icon={<DatabaseOutlined />}
                onClick={handleOpenKnowledgeHub}
                size="small"
                type="text"
              />
              {conditionalValue(workspaceOpenButtonVisible, (
                <Tooltip
                  title="工作台（⌘/Ctrl + Shift + Space）"
                  placement="bottom"
                  classNames={{ root: 'flare-workspace-inline-tooltip' }}
                >
                  <Badge dot={hasUnseenWorkspaceUpdate}>
                    <Button
                      aria-label="打开工作台"
                      className={[
                        'flare-workspace-icon-action flare-workspace-workbench-trigger',
                        conditionalValue(showCanvasPanel, 'is-active', ''),
                      ].filter(Boolean).join(' ')}
                      icon={<PanelRight size={15} strokeWidth={1.8} />}
                      onClick={handleToggleWorkspacePanel}
                      size="small"
                      type="text"
                    />
                  </Badge>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Drawer
        title={firstTruthy(labels.tab_sources, '资料')}
        onClose={() => handleKnowledgeHubOpenChange(false)}
        open={knowledgeHubPopoverOpen}
        placement="right"
        size={360}
        rootClassName="flare-workspace-library-drawer"
        rootStyle={{ ...buildChatThemeCssVars(themeTokens), '--flare-divider': themeTokens.divider,
          fontFamily: themeTokens.fontFamilyBody, color: themeTokens.textPrimary, fontSize: 14 }}
        styles={{
          wrapper: { maxWidth: '100vw' },
          header: { background: themeTokens.surfaceBg, borderColor: themeTokens.divider, padding: '16px 20px' },
          body: { background: themeTokens.surfaceBg, color: themeTokens.textPrimary, padding: 20 },
        }}
      >
        {knowledgeHubPopoverContent}
      </Drawer>
    </div>
  );
}
